import * as Actions from '../actions/optionActions';
import Option from '../../entities/option';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type OptionAction = {
    type: string;
    option: Option | null; 
    optionId: string | Option; 
    partial: boolean; 
    lastFetch: number; 
    options: Option[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Option> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Option[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Option> | null, firmId: string, userId: string, updates?: Option[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'option', 'options', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ optionReducer: backup write for 'options' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ optionReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Option>} data
 * @param {Option[]} [updates]
 * @returns {ArrayMap<Option>}
 */
function updateStorage(data: ArrayMap<Option>, updates?: Option[]): ArrayMap<Option> {
	if (logging) console.debug('⮂ optionReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Option[]} [updates]
 * @returns {ArrayMap<Option>}
 */
function updateStorageNoReturn(updates?: Option[]): void {
	if (logging) console.debug('⮂ optionReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Option[]} [data]
 * @returns {ArrayMap<Option>}
 */
function createState(data?: Option[] | ArrayMap<Option>): ArrayMap<Option> {
	if (!data) return ArrayMapRegistry.clone<Option>('option', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Option>('option', data.map(e => e));
    else return ArrayMapRegistry.clone<Option>('option', data);
}

// Whether deleted options keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Option>} data
 * @returns {Array<Option | OptionDeletionRecord>}
 */
function backupRows(data: ArrayMap<Option>): Array<Option | OptionDeletionRecord> {
	const records: OptionDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({optionId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a option was deleted at a
 * version, never a live row. The storage worker writes one for a deleted option whose type does
 * not keep deleted rows. OWA-68
 */
type OptionDeletionRecord = {optionId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is OptionDeletionRecord => (o as OptionDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a option to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Option>} st
 * @param {Option | OptionDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<Option>, incoming: Option | OptionDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.optionId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as Option);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<Option>} from
 * @returns {ArrayMap<Option>}
 */
function carryOver(from: ArrayMap<Option>): ArrayMap<Option> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function options(state: ArrayMap<Option>, action: OptionAction): ArrayMap<Option> {
	if (!state) state = createState();

	let st: ArrayMap<Option>;

	switch (action.type) {
		// action.option - the new value of option with id action.id
		// action.optionId - id of the target, used if new value is null
		case Actions.OPTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ optionReducer: FETCH_ONE_SUCCESS', action);

			if (action.option === null) return state;

			// Only for a option this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.optionId) || state.deletedVersion(action.option.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.option)) return state;

				updateStorageNoReturn([action.option]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.options
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.options
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.options
			 !partial, !stateEmpty, fetchEmpty	-> D return options in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge options in state newer than those in action.options
		 */
		case Actions.OPTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.options.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Option | OptionDeletionRecord> = action.options.map(option => isDeletionRecord(option) ? option : new Option({...option, persisted: true}));
			const fetchedIds = new Set(fetched.map(option => isDeletionRecord(option) ? option.optionId : option.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<Option>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ optionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} options with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ optionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(option => applyVersion(st, option));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ optionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Option> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Option[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(option => {
					if (applyVersion(st, option)) toUpdate.push(option as Option);
					else if (logging) console.debug('⮂ optionReducer: partialMerge: rejected older version of option');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ optionReducer: empty: options', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ optionReducer: fullMerge: options', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(option => applyVersion(st, option));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.OPTION_FETCH_ONE_FAILURE:
			console.error('OPTION_FETCH_ONE_FAILURE', action.optionId, action.err);

			// No update to storage required
			return state;

		case Actions.OPTION_FETCH_FAILURE:
			console.error('OPTION_FETCH_FAILURE', action.optionId, action.err);

			// No update to storage required
			return state;

		case Actions.OPTION_RESTORE: {
		    let option = new Option({...action.option});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, option)) return state;

            return updateStorage(st, [option]);
        }
            
		case Actions.OPTION_SAVE: {
		    // console.debug('⮂ OPTION_SAVE reducer', action.option?.id);
		    
			// clone the option
			let option = new Option({...action.option});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new option in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, option)) return state;

			return updateStorage(st, [option]);
		}

		case Actions.OPTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ OPTION_SAVE_MANY reducer', action.options.map(option => option.id).join(','));
		    
			// clone the options
			let toProcess = action.options.map(option => new Option(option));

			st = state.clone();
			// state.clear();

			const updates: Option[] = [];

			toProcess.forEach(option => {
				// Add or update the new option in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, option)) updates.push(option);
				else if (logging) console.debug('⮂ OPTION_SAVE_MANY: rejected older version of option');
			});

			return updateStorage(st, updates);
		}

		case Actions.OPTION_REMOVE: {
			// clone the option; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const option = new Option({...action.option, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, option)) return state;

			return updateStorage(st, [option]);
		}

		case Actions.OPTION_RESTORE_FAILURE:
		case Actions.OPTION_SAVE_FAILURE: {
		    console.warn('OPTION_SAVE_FAILURE reducer', action.option?.id);
		    if (!action.option?.id) {
				console.warn('OPTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const option = ensure(Option, action.option);
			if (!option) {
				console.error('Could not find option after call to save');
				return state;
			}

			st = state.clone();

			const _optionRef = state.get(option.id);

			if (_optionRef && (_optionRef.addedOn === option.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _option = new Option({..._optionRef});
				_option.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_option.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('option', option.addedOn, {fatalError: _option.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ optionReducer: failed to save option with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('option', _option).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected option: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected option: ' + err.message));
						}
						else console.debug('⮂ optionReducer: saved option with fatalError info');
					}).catch(err => console.error(`⮂ optionReducer: failed to save option with fatalError info: ${err.message}`));

				st.set(_option);
			}

			return updateStorage(st);
		}

		case Actions.OPTION_SAVE_MANY_FAILURE: {
		    console.warn('OPTION_SAVE_MANY_FAILURE reducer', action.options.map(option => option.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.OPTION_RESTORE_SUCCESS:
		case Actions.OPTION_SAVE_SUCCESS: {
			if (!action.option?.id) {
				console.warn('OPTION_SAVE_SUCCESS reducer');
                return state;
			}

			const optionRef = state.get(action.option?.id);
			if (!optionRef) {
				console.warn('Could not find option after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.option)) return updateStorage(st, [action.option]);

			if (optionRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const option = new Option({...optionRef, persisted: true});
			st.set(option);

			return updateStorage(st, [option]);
		}

		case Actions.OPTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ OPTION_SAVE_MANY_SUCCESS reducer', action.options.map(option => option.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const options: Option[] = [];

			action.options.forEach(e => {
				const optionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!optionRef) console.warn('Could not find option after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) options.push(e);
				else if (!optionRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const option = new Option({...optionRef, persisted: true});
					st.set(option);
					options.push(option);
				}
			});

			return updateStorage(st, options);
		}

		case Actions.OPTION_REMOVE_FAILURE: {
		    console.warn('OPTION_REMOVE_FAILURE action', action.option?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.OPTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.OPTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ OPTION_RESTORE_MANY reducer', action.options.map(option => option.id).join(','));

			const toProcess = action.options.map(option => new Option({...option}));
			st = state.clone();

			const updates: Option[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(option => {
				if (applyVersion(st, option)) updates.push(option);
				else if (logging) console.debug('⮂ OPTION_RESTORE_MANY: rejected older version of option');
			});

			return updateStorage(st, updates);
		}

		case Actions.OPTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ OPTION_REMOVE_MANY reducer', action.options.map(option => option.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.options.map(option => new Option({...option, status: 'D'}));
			st = state.clone();

			const updates: Option[] = [];

			toProcess.forEach(option => {
				if (applyVersion(st, option)) updates.push(option);
				else if (logging) console.debug('⮂ OPTION_REMOVE_MANY: rejected older version of option');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default options;